import Client from "../../../../lib/uma/clients";
import LoadingCircle from "../../../misc/Loading";

/**
 * The user picker for a new Access.
 *
 * Every box is CONTROLLED — `checked` is derived from `selectedUserIds`, which the parent
 * owns and submits. Previously the boxes were `defaultChecked` (appearance owned by the DOM)
 * while the parent's selection state started `undefined` and only ever removed, so the two
 * disagreed from first render: unticking anyone submitted an empty selection while every
 * other box still looked ticked, and the grant silently reached nobody. What you see ticked
 * is now, by construction, what gets granted.
 */
interface IUsersCheckboxesProps {
  clients: Array<Client>;
  /** Doc ids of the selected users. The parent seeds this with everyone. */
  selectedUserIds: Array<string>;
  onSelectionChange: (selectedUserIds: Array<string>) => void;
}

function UsersCheckboxes(props: IUsersCheckboxesProps) {
  const { clients, selectedUserIds, onSelectionChange } = props;

  const toggleClient = (docId: string, isChecked: boolean) => {
    if (isChecked) {
      // Guard against double-adds; `checked` should make this unreachable, but the
      // selection is the thing being submitted, so it stays a set by construction.
      if (selectedUserIds.includes(docId)) return;
      onSelectionChange([...selectedUserIds, docId]);
    } else {
      onSelectionChange(selectedUserIds.filter((id) => id !== docId));
    }
  };

  if (clients === undefined) {
    return <LoadingCircle />;
  } else if (clients.length === 0) {
    return (
      <div
        className="alert alert-info"
        style={{ marginTop: "1.25rem", marginBottom: "0.5rem" }}
      >
        No clients found. You can still create the access and then create
        the clients and assign them this access afterwards.
      </div>
    );
  }

  return (
    <div>
      <p style={{ marginTop: "1.25rem", marginBottom: "0.5rem" }}>
        Select users to give the new access to:
      </p>
      {clients.map((client, i) => {
        return (
          <div key={i} style={{ display: "flex" }}>
            <div
              className="input-group mb-3"
              title={`Toggle access to ${client.email}`}
            >
              <div className="input-group-prepend">
                <div className="input-group-text">
                  <input
                    onChange={(e) => toggleClient(client.docId, e.target.checked)}
                    id={client.docId}
                    type="checkbox"
                    aria-label="Checkbox for following text input"
                    checked={selectedUserIds.includes(client.docId)}
                  />
                </div>
              </div>
              <label htmlFor={client.docId} className="form-control">
                {client.email}
              </label>
            </div>
          </div>
        )
      })}
    </div>
  );
}

export default UsersCheckboxes;
export {};
